import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Button } from '@/components/Elements';
import { DEFAULT_LANG } from '@/config';
import {
  DEFAULT_COUNTRY_ID,
  DEFAULT_GROUP_ID,
  DEFAULT_USER_LANGUAGE_FOR_CONTACT,
  DEFAULT_USER_NAME,
} from '@/config/appConstants';
import { AppLanguages, BaseFormEntity } from '@/types';
import { checkPhone, getInputStyleClasses, renderField, renderSelect } from '@/utils/forms';

export type FormContactProps = {
  className?: string;
  initialValues?: iFormContactFields | undefined;
  onSubmit: (values: iFormContactFields) => void;
  groupId: string;
  name: string;
  email?: string;
  origin: string;
  product: string;
  countryId: string;
  phone: string;
  showCancel?: boolean;
  onCancelButton?: () => void;
};

export type iFormContactFields = {
  email: string;
  description: string;
  phone: string;
  groupId: string;
  name: string;
  origin: string;
  product: string;
  countryId: string;
  language: string;
} & BaseFormEntity;

const initialValuesDefault: iFormContactFields = {
  email: '',
  description: '',
  phone: '',
  groupId: DEFAULT_GROUP_ID,
  name: DEFAULT_USER_NAME,
  origin: '',
  product: '',
  formName: 'Contact',
  countryId: DEFAULT_COUNTRY_ID,
  language: DEFAULT_USER_LANGUAGE_FOR_CONTACT,
};

export const FormContact = ({
  className,
  onSubmit,
  initialValues,
  groupId,
  name,
  email,
  origin,
  product,
  countryId,
  phone,
  showCancel,
  onCancelButton,
}: FormContactProps) => {
  const { t, i18n } = useTranslation();

  initialValuesDefault.groupId = groupId;
  initialValuesDefault.name = name;
  initialValuesDefault.email = email || '';
  initialValuesDefault.origin = origin;
  initialValuesDefault.product = product;
  initialValuesDefault.phone = phone || '';
  initialValuesDefault.countryId = countryId || '';
  initialValuesDefault.language = i18n.language || '';
  const formik = useFormik({
    initialValues: {
      ...initialValuesDefault,
      ...initialValues,
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t('Forms.errorEmail')).required(t('Forms.required')),
      description: Yup.string()
        .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
        .max(500, `${t('Forms.maxErrorChars')} 500 ${t('Forms.chars')}`)
        .required(t('Forms.required')),
      phone: Yup.string()
        .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
        .test('test-phone', t('Forms.invalidFormat'), checkPhone)
        .required(t('Forms.required')),
      groupId: Yup.string().required('Forms.required'),
      name: Yup.string(),
      origin: Yup.string().required(t('Forms.required')),
      product: Yup.string().required(t('Forms.required')),
    }),
    onSubmit: onSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className={className}>
      <h3 className="md__payments__form--h3">{t('Forms.Contact.title')}</h3>
      {renderField(formik, 'groupId', { type: 'hidden' })}
      {renderField(formik, 'name', { type: 'hidden' })}
      {renderField(formik, 'language', { type: 'hidden', value: i18n.language })}
      {renderField(formik, 'country', { type: 'hidden', value: countryId })}
      {renderField(formik, 'origin', { type: 'hidden', value: origin })}
      {renderField(formik, 'product', { type: 'hidden', value: product })}
      <div className="mb-3 mt-5">
        {renderField(formik, 'email', { type: 'email', required: true })}
      </div>
      <div className="mb-3 mt-5">
        {renderSelect(formik, 'phone', {
          type: 'phone',
          required: true,
          renderLabel: (item: { name: string }) => item.name,
          userLanguage: (i18n.language || DEFAULT_LANG) as AppLanguages,
        })}
      </div>
      <div className="mb-3 mt-5">
        <div className="font-medium tracking-wide">
          <label htmlFor="description">{t('Forms.Contact.description')}*</label>
          {formik.touched.description && formik.errors.description ? (
            <div className="text-md-red text-xs">{formik.errors.description}</div>
          ) : null}
        </div>
        <textarea
          id="description"
          name="description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={getInputStyleClasses(
            !!(formik.touched.description && formik.errors.description),
            'textarea'
          )}
          value={formik.values.description}
          rows={11}
        />
      </div>
      <div className="mb-3 flex justify-between">
        <Button variant="primary" type="submit">
          {t('Forms.send')}
        </Button>
        {showCancel && (
          <Button variant="ghost" type="button" onClick={onCancelButton}>
            {t('Forms.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
};
